import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Users } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export interface OrgChartPerson {
  id: string
  displayName: string
  employeeCode: string
  designationName: string | null
  departmentName: string | null
  directReportCount: number
  reports: OrgChartPerson[]
}

export interface OrgChartProps {
  roots: OrgChartPerson[]
  onSelect?: (id: string) => void
  /** Told when a branch is opened whose children were not sent, so the caller can fetch. */
  onExpandBeyondDepth?: (id: string) => void
  className?: string
}

/**
 * The reporting line, as a tree.
 *
 * <p>Indented rows rather than boxes and connectors: a 1,200-person chart drawn as a graph is
 * unreadable and unprintable, and the question people actually bring to it — "who does this
 * person report to, and who reports to them" — is a tree question.
 *
 * <p>Collapsed by default below the roots. A search filters to matching people **and keeps
 * their line of management**, because a match with no context does not answer anything.
 */
export function OrgChart({ roots, onSelect, onExpandBeyondDepth, className }: OrgChartProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(roots.map((r) => r.id)))
  const [search, setSearch] = useState('')

  const needle = search.trim().toLowerCase()

  /** Keeps a node when it matches, or when anybody below it does. */
  const filter = useMemo(() => {
    function keep(node: OrgChartPerson): OrgChartPerson | null {
      const matches = !needle
        || node.displayName.toLowerCase().includes(needle)
        || node.employeeCode.toLowerCase().includes(needle)
        || (node.designationName ?? '').toLowerCase().includes(needle)
      const children = node.reports.map(keep).filter((child): child is OrgChartPerson => child !== null)
      if (matches || children.length > 0) {
        return { ...node, reports: children }
      }
      return null
    }
    return roots.map(keep).filter((node): node is OrgChartPerson => node !== null)
  }, [roots, needle])

  function toggle(node: OrgChartPerson) {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(node.id)) {
        next.delete(node.id)
      } else {
        next.add(node.id)
        // The server sends a bounded depth; opening a leaf that claims reports asks for more.
        if (node.reports.length === 0 && node.directReportCount > 0) {
          onExpandBeyondDepth?.(node.id)
        }
      }
      return next
    })
  }

  function render(node: OrgChartPerson, depth: number) {
    const isOpen = expanded.has(node.id) || Boolean(needle)
    return (
      <li key={node.id}>
        <div
          className="flex items-center gap-2 rounded-md py-1.5 hover:bg-[var(--color-page)]"
          style={{ paddingInlineStart: `${depth * 1.25}rem` }}
        >
          {node.directReportCount > 0 ? (
            <button
              type="button"
              aria-expanded={isOpen}
              aria-label={isOpen ? `Collapse ${node.displayName}` : `Expand ${node.displayName}`}
              className="grid size-5 shrink-0 place-items-center text-[var(--color-muted)]"
              onClick={() => toggle(node)}
            >
              {isOpen
                ? <ChevronDown className="size-4" aria-hidden="true" />
                : <ChevronRight className="size-4" aria-hidden="true" />}
            </button>
          ) : (
            <span className="size-5 shrink-0" />
          )}

          <button
            type="button"
            className="text-start text-sm underline-offset-2 hover:underline"
            onClick={() => onSelect?.(node.id)}
          >
            <span className="font-medium text-[var(--color-heading)]">{node.displayName}</span>
            <span className="ms-2 font-mono text-xs text-[var(--color-muted)]">
              {node.employeeCode}
            </span>
            {node.designationName && (
              <span className="ms-2 text-xs text-[var(--color-muted)]">
                {node.designationName}
              </span>
            )}
          </button>

          {node.directReportCount > 0 && (
            <span className="ms-auto flex items-center gap-1 text-xs text-[var(--color-muted)]">
              <Users className="size-3.5" aria-hidden="true" />
              {node.directReportCount}
            </span>
          )}
        </div>

        {isOpen && node.reports.length > 0 && (
          <ul>{node.reports.map((child) => render(child, depth + 1))}</ul>
        )}
        {isOpen && node.reports.length === 0 && node.directReportCount > 0 && (
          <p
            className="py-1 text-xs text-[var(--color-muted)]"
            style={{ paddingInlineStart: `${(depth + 1) * 1.25}rem` }}
          >
            {node.directReportCount} report(s) below this level are not loaded yet.
          </p>
        )}
      </li>
    )
  }

  return (
    <div className={className}>
      <div className="mb-3 max-w-sm">
        <Label htmlFor="org-chart-search">Find somebody</Label>
        <Input
          id="org-chart-search"
          value={search}
          placeholder="Name, code or designation"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {filter.length === 0 ? (
        <p className="text-sm text-[var(--color-muted)]">
          {needle
            ? 'Nobody matches, so no line of management is shown either.'
            : 'Nobody has been placed under a manager yet, so there is no chart to draw.'}
        </p>
      ) : (
        <ul>{filter.map((root) => render(root, 0))}</ul>
      )}
    </div>
  )
}
